// src/encounter/lib/encounter-blocks.ts
// The declarative block/zone model — the Encounter Contract's "one idea" (§0/§3/§4):
// `EncounterShell` is a block HOST, not a set of hand-built screens. Each block declares
// WHERE it sits per modality, WHEN it is present, and WHO may see it; the host computes the
// frame, nothing is hand-placed. Phase/modality/patient-context/role only change which blocks
// are present and where — never the host.
//
// This is the Wave-0 engine seed: the host already resolves the section-nav zone from this
// catalog (so the nav is DATA, not a hardcoded list). Subsequent waves register more blocks
// against the SAME schema (Contract §11) — a re-point, not a rebuild.
import type { HideableStepKey } from './cps-layout';
import { ALLIED_PLAN_STEP, NURSING_PLAN_STEP, type PaStepKey } from './encounter-steps';

export type Modality = 'in-person' | 'virtual';

/** Where a block sits within a modality's frame (Contract §4 zone column). */
export type EncounterZone =
  | 'left-rail' // patient summary / IPS
  | 'center-nav' // the horizontal section tabs
  | 'center' // step content (HPI/ROS/PE/A&P/Docs/Wrap-up)
  | 'right-drawer' // the 4-tab Note/Orders/Timeline/Edit drawer
  | 'overlay' // floating over the virtual video surface
  | 'full-surface' // the televisit panel itself
  | 'hidden'; // not present in this modality

/**
 * The clinical personas (Contract §13a). NOTE: this is a DISPLAY / workflow-routing hint,
 * NOT a permission gate — order/edit permissions remain DATA (allowedOrderTypes), never a
 * hardcoded role matrix. See CLAUDE.md "permissions are DATA".
 */
export type ClinicalRole =
  | 'physician'
  | 'nurse'
  | 'technician'
  | 'coach'
  | 'nutritionist'
  // PROD-2040 — the allied-health clinical access level (`TenantMasterId = 20`). WIDENING, not a
  // split: the five §13a members above are untouched and every existing value still resolves
  // exactly as it did. Kept character-for-character in sync with `ClinicalRole` in
  // `src/auth/use-role.ts` (added there by #372), which is what `clinicalRoleFor` returns.
  //
  // ONE member, not one per discipline. Health coach / dietitian / exercise physiologist are
  // DISCIPLINES — they differ in what they treat, not in what the app lets them do — and a
  // discipline is `speciality` DATA. `'coach'` and `'nutritionist'` above are the DS's own §13a
  // vocabulary and are NOT what an allied-health account resolves to.
  | 'allied-health';

/** The context the host resolves presence + zone against. */
export interface EncounterContext {
  modality: Modality;
  role: ClinicalRole;
  /**
   * The step keys the ACTIVE TEMPLATE removed (cps-layout `hiddenSteps` — see cps-layout.ts).
   * Optional and additive: absent/empty leaves the full role-visible step set untouched. Only
   * `hpi`/`risk`/`ros`/`pe` can ever appear here — `hiddenStepsOf` already strips anything else
   * (notably `ap`, which the template can never remove) before this is populated, so `resolveSteps`
   * does not need to re-enforce that allowlist; it only intersects.
   */
  hiddenSteps?: readonly SuppressibleStepKey[];
  /**
   * [PROD-3376] Does the served template author PE content? The caller computes it with
   * `stepHasContent(questionnaire, 'pe')` (questionnaire-groups.ts — kept out of this module so the
   * catalog stays free of the DS runtime). Read for ONE decision only: whether an ALLIED-HEALTH
   * clinician gets the PE (Objective exam) step — a dietitian's anthropometry, say. Absent or false
   * → no PE step for allied health, exactly as before. Ignored for every other role.
   */
  templateHasPeContent?: boolean;
}

/**
 * What `ctx.hiddenSteps` may name: the template-hideable keys (`hpi`/`risk`/`ros`/`pe`, sanitised by
 * `hiddenStepsOf`) PLUS the nurse's plan step, which the PAGE (never a template) hides while
 * `?ef=planSave` is off ([PROD-2949], design desk D-66). `ap`, `docs`, `wrap-up` and `allied-plan`
 * stay unhideable.
 */
export type SuppressibleStepKey = HideableStepKey | typeof NURSING_PLAN_STEP;

/**
 * Every `ClinicalRole`, derived from an exhaustive `Record` so a new member of the union cannot be
 * added without landing here — the same mechanism `CLINICAL_ROLE_LABELS` uses in `auth/use-role.ts`,
 * and for the same reason: the PROD-2040 family of defects are all a new role silently inheriting
 * an old default.
 */
const CLINICAL_ROLE_KEYS: Record<ClinicalRole, true> = {
  physician: true,
  nurse: true,
  technician: true,
  coach: true,
  nutritionist: true,
  'allied-health': true,
};

/** A block every persona sees. Spelled OUT, because omission is what caused the defect below. */
export const ALL_CLINICAL_ROLES: ClinicalRole[] = Object.keys(CLINICAL_ROLE_KEYS) as ClinicalRole[];

/**
 * The MEDICAL TEAM — the personas whose scope includes diagnostic history-taking, interpreting
 * medical data, and performing a physical examination. It is the gate on HPI / ROS / PE.
 *
 * WHY THIS SET (PROD-2040 follow-up). `ClinicalRole` has six members. Two describe the licensed
 * medical team in THIS app (physician · nurse). The other four describe scope that does not
 * include diagnostic history-taking or physical examination: `'coach'` and `'nutritionist'` are the
 * DS's own §13a vocabulary for a health coach and a dietitian, `'allied-health'` is the PA access
 * level those disciplines actually arrive as, and `'technician'` — see below. A health coach,
 * dietitian or exercise physiologist does not diagnose, does not interpret medical data and does
 * not examine a patient, so those three are excluded for one reason rather than three.
 *
 * `'technician'` is OUT as of the 2026-09-04 ruling: a technician's app is the PRACTICE app (PM),
 * not this one, and a technician may NOT take a history or examine a patient. The previous form of
 * this array included it on the `physician | nurse | technician` precedent in the open #339, noting
 * that "if the ruling is that a technician may not take a history, this array is where that is a
 * one-word edit". This is that edit.
 *
 * Removing it is behaviourally inert TODAY and measured to be: `clinicalRoleFor` returns only
 * `'physician' | 'nurse' | 'allied-health'` and `toClinicalRole` only `'physician' | 'nurse' |
 * 'allied-health'`, so nothing in this app resolves to `'technician'` and no live flow changes
 * either way. It is done anyway because dead config that CONTRADICTS a ruling is not harmless: the
 * next person to wire up a technician persona would have inherited examination rights nobody
 * granted, silently, which is the entire PROD-2040 family of defects.
 *
 * The `'technician'` member of the `ClinicalRole` union above is deliberately KEPT — it is DS
 * §13a vocabulary parity, and removing it is a wider change than this ruling asks for.
 */
export const MEDICAL_TEAM_ROLES: ClinicalRole[] = ['physician', 'nurse'];

/** A block declaration — what it is, where it sits per modality, when present, who sees it. */
export interface EncounterBlockDef {
  id: string;
  /** Zone per modality (Contract §4: in-person vs virtual). */
  zone: { inPerson: EncounterZone; virtual: EncounterZone };
  /** Presence predicate; default = always present (else the engine reflows/promotes). */
  presence?: (ctx: EncounterContext) => boolean;
  /** Promotion order within a zone (lower = earlier). */
  priority?: number;
  /**
   * Roles that may see the block. MANDATORY (PROD-2040 follow-up) — there is deliberately no
   * "leave it off and everybody gets it" default any more. A genuinely role-agnostic block says so
   * with `ALL_CLINICAL_ROLES`; that is a declaration a reviewer can see, and omission was not.
   * A new block cannot now be added to any catalog without answering the question, because `tsc`
   * refuses the object literal until it does.
   */
  roles: ClinicalRole[];
}

/** The shell's coarse role prop. WIDENED by PROD-2040; `'doctor'`/`'nurse'` unchanged. */
export type ShellRole = 'doctor' | 'nurse' | 'allied-health';

/**
 * Map the shell's coarse role prop onto the clinical-persona union.
 *
 * The `'physician'` fallback is deliberately UNCHANGED — anything that is not a recognised
 * non-physician persona still resolves to 'physician', exactly as before. Only the newly
 * recognised `'allied-health'` value is diverted out of it.
 */
export function toClinicalRole(role: ShellRole): ClinicalRole {
  if (role === 'nurse') return 'nurse';
  if (role === 'allied-health') return 'allied-health';
  return 'physician';
}

/** Resolve a block's zone for the active modality. */
export function zoneFor(block: EncounterBlockDef, modality: Modality): EncounterZone {
  return modality === 'virtual' ? block.zone.virtual : block.zone.inPerson;
}

/**
 * Is the block present for this context (role + presence predicate)?
 *
 * FAILS CLOSED (PROD-2040 follow-up). This used to read `if (block.roles && !…includes(…))`, so a
 * block declaring NO roles was present for EVERY role — presence by OMISSION. That is exactly how
 * an allied-health account came to be shown HPI, ROS and Physical Examination: #384 gated the two
 * blocks it was thinking about and the other six were inherited by silence. `roles` is now REQUIRED
 * on `EncounterBlockDef`, so "no gate" is not a state a block can be in; the `?.` is belt-and-braces
 * for a value that got here through a cast, and it resolves to NOT PRESENT too.
 */
export function isPresent(block: EncounterBlockDef, ctx: EncounterContext): boolean {
  if (!block.roles?.includes(ctx.role)) return false;
  if (block.presence && !block.presence(ctx)) return false;
  return true;
}

/** Blocks that render in `zone` for `ctx` — present-filtered and priority-ordered. */
export function blocksForZone(
  blocks: EncounterBlockDef[],
  zone: EncounterZone,
  ctx: EncounterContext,
): EncounterBlockDef[] {
  return blocks
    .filter((b) => zoneFor(b, ctx.modality) === zone && isPresent(b, ctx))
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
}

/** A section-nav block also carries its encounter step key. */
export interface StepBlockDef extends EncounterBlockDef {
  /** `PaStepKey` = the DS `EncounterStepKey` union WIDENED by the PA-local keys — see
   *  encounter-steps.ts. A DS step key is still assignable, so existing blocks are unaffected. */
  step: PaStepKey;
}

/**
 * The encounter section steps as blocks (center-nav zone). Order = priority.
 * A/P is physician-only (nurse runs the flow without it) — encoded here as DATA,
 * not branched in the view. DS-030 step count keys off this.
 *
 * EVERY ROW DECLARES ITS ROLES. No row inherits visibility by omission — see the `roles` doc on
 * `EncounterBlockDef` and the defect it records. The two role sets in play:
 *
 *   MEDICAL_TEAM_ROLES  — HPI · ROS · PE. Diagnostic history-taking, interpreting medical data and
 *                         physical examination. NOT allied health (PROD-2040): a health coach,
 *                         dietitian or exercise physiologist does none of the three.
 *   ALL_CLINICAL_ROLES  — Risk · Docs · Wrap-up. Every persona: `risk` is an authored screening
 *                         section whose content is template DATA (activity readiness, nutrition
 *                         risk and behavioural risk are squarely allied-health work) and it carries
 *                         no diagnosis; `docs` attaches and views documents; `wrap-up` closes the
 *                         encounter, and every role must be able to finish one. Signing-and-LOCKING
 *                         is separately gated by `canLockEncounter` and is unaffected.
 *
 * A/P stays hard-coded physician-only per the founder ruling, and `step-allied-plan` stays
 * allied-health-only. Neither is made configurable here.
 *
 * The physician's and nurse's resolved flows are UNCHANGED by the gating above — `MEDICAL_TEAM_ROLES`
 * contains both — and that is asserted, not assumed, by the exact-array expectations in
 * encounter-blocks.test.ts, encounter-blocks.allied.test.ts and encounter-blocks.role-gating.test.ts.
 */
/** [PROD-3376] Who may receive the PE step: the medical team, plus allied health when the template
 *  authors PE content (gated in `resolveSteps`, never here). */
const PE_STEP_ROLES: ClinicalRole[] = [...MEDICAL_TEAM_ROLES, 'allied-health'];

export const STEP_BLOCKS: StepBlockDef[] = [
  { id: 'step-hpi', step: 'hpi', zone: { inPerson: 'center-nav', virtual: 'center-nav' }, priority: 0, roles: MEDICAL_TEAM_ROLES },
  { id: 'step-risk', step: 'risk', zone: { inPerson: 'center-nav', virtual: 'center-nav' }, priority: 1, roles: ALL_CLINICAL_ROLES },
  { id: 'step-ros', step: 'ros', zone: { inPerson: 'center-nav', virtual: 'center-nav' }, priority: 2, roles: MEDICAL_TEAM_ROLES },
  // PE (Physical Exam) = SOAP Objective exam, after ROS. The medical team only — an allied-health
  // practitioner does not perform a physical examination (PROD-2040).
  // [PROD-3376] Allied health is listed too, but `resolveSteps` keeps it for them ONLY when the served
  // template authors PE content (a dietitian's measurements, drawn under Objective). The encounter
  // owner's ruling; the medical team's row membership is unchanged.
  { id: 'step-pe', step: 'pe', zone: { inPerson: 'center-nav', virtual: 'center-nav' }, priority: 3, roles: PE_STEP_ROLES },
  { id: 'step-docs', step: 'docs', zone: { inPerson: 'center-nav', virtual: 'center-nav' }, priority: 4, roles: ALL_CLINICAL_ROLES },
  { id: 'step-ap', step: 'ap', zone: { inPerson: 'center-nav', virtual: 'center-nav' }, priority: 5, roles: ['physician'] },
  // PROD-2040 — the allied-health PLAN step. It sits in the A/P slot (priority 5) because it is
  // that slot's allied-health counterpart, and the two never co-occur: `roles` is disjoint
  // (`['physician']` vs `['allied-health']`), so no ordering tie between them is ever resolved.
  //
  // A/P above is UNTOUCHED and stays hard-coded physician-only per the founder ruling — this row
  // does not make it configurable, it adds a sibling. The allied-health flow is Risk · Docs · Plan ·
  // Wrap-up: it terminates in `allied-plan` instead of `ap` (allied health documents a PLAN and does
  // not prescribe — see allied-health-plan.ts), and it carries no HPI/ROS/PE, which this row's
  // original form wrongly left it inheriting.
  { id: 'step-allied-plan', step: ALLIED_PLAN_STEP, zone: { inPerson: 'center-nav', virtual: 'center-nav' }, priority: 5, roles: ['allied-health'] },
  // [PROD-2949] The NURSE's plan step (design desk D-66) — the mirror of `step-allied-plan`, in the
  // same A/P slot (priority 5). `roles: ['nurse']` is disjoint from `['physician']` and
  // `['allied-health']`, so the physician's and allied-health flows are byte-identical with or
  // without this row (asserted in encounter-blocks.nursing.test.ts). The page hides it through
  // `ctx.hiddenSteps` while `?ef=planSave` is off, so the nurse's flow is unchanged too until then.
  { id: 'step-nursing-plan', step: NURSING_PLAN_STEP, zone: { inPerson: 'center-nav', virtual: 'center-nav' }, priority: 5, roles: ['nurse'] },
  { id: 'step-wrap-up', step: 'wrap-up', zone: { inPerson: 'center-nav', virtual: 'center-nav' }, priority: 6, roles: ALL_CLINICAL_ROLES },
];

/**
 * Resolve the ordered, role-visible step keys from the step-block catalog, then INTERSECT with the
 * template's `hiddenSteps` (if any). Role/venue visibility stays the catalog's job — the template
 * cannot know the clinician's role, and the catalog encodes founder rulings the template has no
 * authority to override. The template only ever narrows further, and only over the four keys
 * `hiddenStepsOf` allows through (`ctx.hiddenSteps`'s own type already excludes `ap`).
 *
 * FAILS OPEN if every allowed key were somehow hidden: only `hpi`/`risk`/`ros`/`pe` are hideable,
 * so `docs`/`ap`/`wrap-up` (and, for allied-health, `allied-plan`) always survive the intersection
 * and the nav is never left empty.
 */
export function resolveSteps(ctx: EncounterContext): PaStepKey[] {
  const catalog = blocksForZone(STEP_BLOCKS, 'center-nav', ctx).map((b) => (b as StepBlockDef).step);
  // [PROD-3376] Allied health: PE only when the template has PE content. Every other role skips this.
  const resolved =
    ctx.role === 'allied-health' && ctx.templateHasPeContent !== true
      ? catalog.filter((step) => step !== 'pe')
      : catalog;
  if (!ctx.hiddenSteps || ctx.hiddenSteps.length === 0) return resolved;
  const hidden = new Set<PaStepKey>(ctx.hiddenSteps);
  return resolved.filter((step) => !hidden.has(step));
}
